import { Button, Tooltip } from "antd"
import { CheckIcon, ExternalLink, XIcon } from "lucide-react"
import { useCallback, useMemo } from "react"

import type { SheetDiscussion } from "../../discussions/types"
import { isVerbatimFromAnswerBank } from "../../hooks/useEnrichedReference"
import { getColumnNumber } from "../../sheets/utils"
import type { AnswerReference } from "../../types/answerer"
import type {
  AnswerQuestionnaireJob,
  StoredGeneratedAnsweredQuestion,
} from "../../types/jobs"
import type { WorksheetRange } from "../../types/sheets"
import AnswerFeedbackButtons from "../AnswerFeedbackButtons"
import Discussions from "../Discussions/Discussions"
import ReferenceLink from "../ReferenceLink"
import { useGridContext } from "./GridContext"
import { useApprovalHandlers } from "./useApprovalHandlers"

const SURFACE = "WEB_QUESTIONNAIRE_ASSISTANT"

interface Props {
  references?: AnswerReference[]
  activeGroupOid: string
  selectedSheet: string
  job: AnswerQuestionnaireJob
  discussions?: SheetDiscussion[]
  answer?: StoredGeneratedAnsweredQuestion
  discussionsError?: Error
  location: WorksheetRange
}

const CellSidebarPane: React.FC<Props> = ({
  references,
  activeGroupOid,
  selectedSheet,
  job,
  discussions,
  answer,
  discussionsError,
  location,
}) => {
  const { cellFocused, onClickLocation } = useGridContext()
  const { approving, handleApproveMany, handleUnapproveMany } =
    useApprovalHandlers(job.oid)

  const handleApprove = useCallback(async () => {
    if (!answer) return
    await handleApproveMany([answer], false)
  }, [answer, handleApproveMany])

  const handleUnapprove = useCallback(async () => {
    if (!answer) return
    await handleUnapproveMany([answer])
  }, [answer, handleUnapproveMany])

  const filteredDiscussions = useMemo(() => {
    if (!discussions?.length || !cellFocused) return
    return discussions.filter((discussion) => {
      const range = discussion.location
      if (!range) return false

      return (
        range.sheetName === selectedSheet &&
        getColumnNumber(cellFocused.colId) === range.firstColIndex &&
        cellFocused.rowIndex + 1 === range.firstRowIndex
      )
    })
  }, [cellFocused, discussions, selectedSheet])

  return (
    <>
      {references?.length ? (
        <div>
          <h3>
            <span className="mr-1 font-bold text-purple-500">
              {`${cellFocused?.colId}${(cellFocused?.rowIndex ?? 0) + 1}`}
            </span>
            References
          </h3>
          {references.map((r, idx) => (
            <ReferenceLink
              index={idx}
              key={idx}
              reference={r}
              loadEagerly
              isVerbatimFromAnswerBank={isVerbatimFromAnswerBank(references)}
            />
          ))}
        </div>
      ) : answer ? (
        <div className="mt-4">No references to display</div>
      ) : null}

      {answer ? (
        <div>
          <div className="mt-2 flex gap-2">
            <AnswerFeedbackButtons
              surface={SURFACE}
              answer={answer}
              answererRequestId={answer.last_quilt_answer_request_id}
            />
          </div>

          <div>
            <h3 className="mb-0 mt-4">Approval</h3>
            {answer.last_reviewed_by ? (
              <div className="text-sm flex items-center gap-2 bg-green-200 p-1">
                <Tooltip title="Remove Approval">
                  <Button
                    size="small"
                    icon={<XIcon />}
                    onClick={handleUnapprove}
                    loading={approving}
                    disabled={approving}
                  />
                </Tooltip>
                <span className="font-semibold">Approved By:</span>
                <span>{answer.last_reviewed_by?.email}</span>
              </div>
            ) : (
              <div className="text-sm flex items-center gap-2 bg-yellow-50 p-1">
                <Tooltip title="Approve Answer">
                  <Button
                    size="small"
                    icon={<CheckIcon />}
                    onClick={handleApprove}
                    loading={approving}
                    disabled={approving}
                  />
                </Tooltip>
                <span>Not yet approved</span>
              </div>
            )}
          </div>

          <Tooltip title="Ownership and other settings can be managed in the list view.">
            <a
              href={`/questionnaire-assistant/review/feed/${job.oid}?oid=${answer.oid}`}
              target="_blank"
              className="text-primary mt-4 flex items-center gap-2 font-semibold underline w-fit"
            >
              <ExternalLink size={16} /> Open in List View
            </a>
          </Tooltip>
        </div>
      ) : null}

      <Discussions
        group_oid={activeGroupOid}
        discussions={filteredDiscussions}
        job_id={job.oid}
        location={location}
        response_oid={answer?.oid}
        kind="SHEET"
        discussionsError={discussionsError}
        onClickLocation={onClickLocation}
      />
    </>
  )
}

export default CellSidebarPane
